import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { stopWatch, useWatchRun, watchPhase } from '../../lib/watchMode.js';
import './watch-overlay.css';

/**
 * A repetição do modo Assistir (F1): abre a tela da ação, mostra a folha com os campos sendo
 * escritos e o botão apertado, e acende o item novo na página. Fica em cada moldura da clínica
 * e segue do ponto certo quando a tela troca (o começo é guardado fora). "Pular" encerra.
 */
export function WatchOverlay() {
  const run = useWatchRun();
  const [location, navigate] = useLocation();
  const [now, setNow] = useState(() => Date.now());

  // Abre a tela da ação (o resto do endereço, como #recados, a própria página trata).
  useEffect(() => {
    if (!run) return;
    const [path] = run.script.href.split('#');
    const here = `${location}${window.location.search}`;
    if (path && here !== path) navigate(path);
    // Só quando começa uma repetição nova.
  }, [run?.id]);

  useEffect(() => {
    if (!run) return;
    let frame = 0;
    const tick = () => {
      setNow(Date.now());
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [run?.id]);

  const phase = run ? watchPhase(run, now) : null;

  // Enquanto a folha é preenchida, o item novo (já gravado) fica escondido na página.
  const sheetOpen = Boolean(phase?.sheet && !phase.closing);
  useEffect(() => {
    if (!run?.script.item) return;
    const selector = `[data-sec-item="${CSS.escape(run.script.item)}"]`;
    const apply = () =>
      document.querySelectorAll<HTMLElement>(selector).forEach((row) => {
        row.classList.toggle('sec-watch-wait', sheetOpen);
      });
    apply();
    if (!sheetOpen) return;
    // A página pode desenhar o item depois (busca de novo): confere enquanto a folha está aberta.
    const timer = window.setInterval(apply, 120);
    return () => {
      window.clearInterval(timer);
      document
        .querySelectorAll<HTMLElement>(selector)
        .forEach((row) => row.classList.remove('sec-watch-wait'));
    };
  }, [run?.id, sheetOpen]);

  // No fim, acende o item novo (consulta, bloqueio) ou o horário, e a repetição acaba.
  useEffect(() => {
    if (!run || !phase?.highlighting) return;
    const { item, slot } = run.script;
    const target =
      (item && document.querySelector<HTMLElement>(`[data-sec-item="${CSS.escape(item)}"]`)) ||
      (slot && document.querySelector<HTMLElement>(`[data-sec-slot="${CSS.escape(slot)}"]`)) ||
      (run.script.href.includes('#')
        ? document.getElementById(run.script.href.split('#')[1] ?? '')
        : null);
    if (!target) return;
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    target.classList.add('sec-watch-hit');
    const timer = window.setTimeout(() => target.classList.remove('sec-watch-hit'), 2400);
    return () => window.clearTimeout(timer);
  }, [run?.id, phase?.highlighting]);

  useEffect(() => {
    if (phase?.done) stopWatch();
  }, [phase?.done]);

  if (!run || !phase) return null;
  return (
    <div className="wo" role="status" aria-live="polite">
      <span className="wo-badge">
        <i aria-hidden="true" />
        Assistindo
        <button type="button" onClick={stopWatch}>
          Pular
        </button>
      </span>
      {phase.sheet && (
        <div className={`wo-sheet${phase.closing ? ' closing' : ''}`} aria-label={run.script.title}>
          <b className="wo-title">{run.script.title}</b>
          {phase.typed.map((row) => (
            <label key={row.name} className="wo-field" data-sec-field={row.name}>
              <span>{row.label}</span>
              <b className={row.typing ? 'typing' : ''}>
                {row.shown}
                {row.typing && <i className="wo-caret" aria-hidden="true" />}
              </b>
            </label>
          ))}
          <span className={`wo-press${phase.pressing ? ' down' : ''}`}>{run.script.press}</span>
        </div>
      )}
    </div>
  );
}
